/**
 * Grid card for a course with progress, last-learned, and continue actions.
 */
import React from "react";
import { Icon } from "@iconify/react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { VButton, VText } from "form";
import { calcProgressValue } from "utils/grade.util";
import { getCourseImageUrl } from "utils/string";
import BThumb from "./BThumb";

/**
 * Course tile that links to the course player or assignment inbox.
 *
 * @param {{ course: Object, isProgress?: boolean }} props
 */
const BCourseGridItem = (props) => {
  const { course, isProgress = true } = props;

  const { t } = useTranslation("common");
  const navigate = useNavigate();

  /**
   * Navigates to the course overview.
   *
   * @param {string} courseId Course identifier.
   * @returns {void}
   */
  const onClick = (courseId) => navigate(`/course/${courseId}`);
  /**
   * Navigates to the course guide.
   *
   * @param {string} courseId Course identifier.
   * @returns {void}
   */
  const onGuideClick = (courseId) => navigate(`/guide/${courseId}`);

  return (
    <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 h-full flex flex-col">
      <Box className="flex flex-col w-full">
        <BThumb src={getCourseImageUrl(course.course_image_url)} className="w-full aspect-[9/5] rounded-md" />
        {isProgress && (
          <Box className="w-full bg-gray-200 h-1.5 dark:bg-gray-700">
            <Box className="bg-green-600 h-1.5" sx={{ width: `${calcProgressValue(course)}%` }} />
          </Box>
        )}
      </Box>
      <Box className="p-4 flex flex-col gap-1 h-full">
        <VText className="text-xl text-limit-2">{course.display_name || "-"}</VText>
        {course.enrollment_status ? (
          <>
            <VText color="secondary" className="text-sm text-limit-1" div>
              <Icon icon="lucide:clock-9" className="flex-shrink-0 align-middle" />
              &nbsp;{course.last_learned ? `${t("Last learned")} ${Math.round((new Date() - course.last_learned) / (1000 * 60))} min ${t("ago")}` : t("You did not study after login!")}
            </VText>
            <VButton color="secondary" className="flex items-center text-sm mb-2 justify-start px-0" onClick={() => onGuideClick(course.id)}>
              <Icon icon="lucide:file-search" />
              &nbsp;
              <VText color="custom" className="text-red-500">
                {t("View Guide")}
              </VText>
            </VButton>
            <VButton variant="contained" color="primary" className="w-full normal-case mt-auto" onClick={() => onClick(course.id)}>
              {t("Continue")}
            </VButton>
          </>
        ) : (
          <Link to="/inbox" className="no-underline mt-auto">
            <VButton variant="outlined" color="primary" className="w-full normal-case mt-auto" onClick={() => onClick(course.id)}>
              {t("Require Assign")}
            </VButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};

BCourseGridItem.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.string.isRequired,
    display_name: PropTypes.string.isRequired,
    enrollment_status: PropTypes.number,
    last_learned: PropTypes.instanceOf(Date),
    course_image_url: PropTypes.string.isRequired,
  }).isRequired,
  isProgress: PropTypes.bool,
};

export default BCourseGridItem;
